import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  ActionIcon,
  Button,
  Card,
  Group,
  MultiSelect,
  NumberInput,
  SimpleGrid,
  Stack,
  Table,
  Text,
} from '@mantine/core'
import { IconTrash } from '@tabler/icons-react'
import { Link } from 'react-router-dom'
import { showNotification } from '@mantine/notifications'
import { api, ApiError, DEFAULT_ERROR_NOTIFY_MESSAGE } from '@/lib/api'
import type {
  LoadTestErrorsResponse,
  LoadTestProjectOption,
  LoadTestSessionStatus,
  LoadTestStatusResponse,
} from '@/types/api'

type ProjectRow = {
  projectId: string
  name: string
  tph: number | string
  msgMin: number | string
  msgMax: number | string
}

const DEFAULT_TPH = 150
const DEFAULT_MSG_MIN = 4
const DEFAULT_MSG_MAX = 30

function num(v: number | string, fallback: number) {
  return typeof v === 'number' ? v : parseInt(String(v), 10) || fallback
}

export function SettingsLoadTestTab() {
  const [projects, setProjects] = useState<LoadTestProjectOption[]>([])
  const [status, setStatus] = useState<LoadTestStatusResponse | null>(null)
  const [errorsBySession, setErrorsBySession] = useState<Record<string, LoadTestErrorsResponse>>({})
  const [errorsPageBySession, setErrorsPageBySession] = useState<Record<string, number>>({})
  const [loading, setLoading] = useState(true)
  const [starting, setStarting] = useState(false)
  const [stoppingId, setStoppingId] = useState<string | null>(null)
  const [stoppingAll, setStoppingAll] = useState(false)

  const [selectedRows, setSelectedRows] = useState<ProjectRow[]>([])
  const [addProjectIds, setAddProjectIds] = useState<string[]>([])
  const [defaultTph, setDefaultTph] = useState<number | string>(DEFAULT_TPH)
  const [defaultMsgMin, setDefaultMsgMin] = useState<number | string>(DEFAULT_MSG_MIN)
  const [defaultMsgMax, setDefaultMsgMax] = useState<number | string>(DEFAULT_MSG_MAX)

  const sessions = status?.sessions ?? []
  const running = status?.status === 'running'
  const runningSessions = sessions.filter((s) => s.status === 'running')
  const runningProjectIds = useMemo(
    () => new Set(runningSessions.map((s) => s.project_id).filter(Boolean)),
    [runningSessions],
  )

  const refresh = useCallback(async () => {
    setLoading(true)
    try {
      const [p, s] = await Promise.all([api.loadTestProjects(), api.loadTestStatus()])
      setProjects(Array.isArray(p) ? p : [])
      setStatus(s || null)

      const nextErrors: Record<string, LoadTestErrorsResponse> = {}
      for (const sess of s?.sessions ?? []) {
        const sid = sess.session_id
        if (!sid || (sess.tickets_failed ?? 0) <= 0) continue
        const page = errorsPageBySession[sid] ?? 1
        try {
          nextErrors[sid] = await api.loadTestErrors(sid, { page, per_page: 10 })
        } catch {
          nextErrors[sid] = { items: [], total: 0, page: 1, per_page: 10 }
        }
      }
      setErrorsBySession(nextErrors)
    } catch (e) {
      showNotification({
        title: 'Ошибка',
        message: e instanceof ApiError ? e.message : 'Не удалось загрузить',
        color: 'red',
      })
    } finally {
      setLoading(false)
    }
  }, [errorsPageBySession])

  useEffect(() => {
    void refresh()
  }, [refresh])

  useEffect(() => {
    if (!running) return
    const id = window.setInterval(() => void refresh(), 60000)
    return () => window.clearInterval(id)
  }, [running, refresh])

  const availableToAdd = useMemo(() => {
    const used = new Set(selectedRows.map((r) => r.projectId))
    return projects.filter((p) => !used.has(p.id) && !runningProjectIds.has(p.id))
  }, [projects, selectedRows, runningProjectIds])

  const addProjectsFromSelect = () => {
    if (!addProjectIds.length) return
    const tph = num(defaultTph, DEFAULT_TPH)
    const msgMin = num(defaultMsgMin, DEFAULT_MSG_MIN)
    const msgMax = num(defaultMsgMax, DEFAULT_MSG_MAX)
    const newRows: ProjectRow[] = []
    for (const id of addProjectIds) {
      const p = projects.find((x) => x.id === id)
      if (!p || selectedRows.some((r) => r.projectId === id)) continue
      newRows.push({ projectId: id, name: p.name, tph, msgMin, msgMax })
    }
    setSelectedRows((prev) => [...prev, ...newRows])
    setAddProjectIds([])
  }

  const applyDefaultsToAll = () => {
    const tph = num(defaultTph, DEFAULT_TPH)
    const msgMin = num(defaultMsgMin, DEFAULT_MSG_MIN)
    const msgMax = num(defaultMsgMax, DEFAULT_MSG_MAX)
    setSelectedRows((rows) => rows.map((r) => ({ ...r, tph, msgMin, msgMax })))
  }

  const updateRow = (projectId: string, patch: Partial<ProjectRow>) => {
    setSelectedRows((rows) => rows.map((r) => (r.projectId === projectId ? { ...r, ...patch } : r)))
  }

  const removeRow = (projectId: string) => {
    setSelectedRows((rows) => rows.filter((r) => r.projectId !== projectId))
  }

  const start = async () => {
    const rowsToStart = selectedRows.filter((r) => !runningProjectIds.has(r.projectId))
    if (!rowsToStart.length) {
      const msg = selectedRows.length
        ? 'Выбранные проекты уже в тесте — добавьте другие или остановите текущие'
        : 'Добавьте хотя бы один проект'
      showNotification({ title: 'Форма', message: msg, color: 'yellow' })
      return
    }
    const payload = {
      projects: rowsToStart.map((r) => ({
        project_id: r.projectId,
        tickets_per_hour: num(r.tph, DEFAULT_TPH),
        messages_per_ticket_min: num(r.msgMin, DEFAULT_MSG_MIN),
        messages_per_ticket_max: num(r.msgMax, DEFAULT_MSG_MAX),
      })),
    }
    setStarting(true)
    try {
      const res = await api.loadTestStart(payload)
      const started = res.started_count ?? res.sessions?.length ?? 0
      const errList = res.errors?.filter(Boolean) ?? []
      if (started > 0) {
        showNotification({
          title: 'Тест запущен',
          message: errList.length ? `Запущено: ${started}. ${errList.join('; ')}` : `Проектов: ${started}`,
          color: errList.length ? 'yellow' : 'green',
        })
      } else {
        showNotification({ title: 'Не запущено', message: errList.join('; ') || 'Ошибка', color: 'red' })
      }
      const startedIds = new Set((res.sessions ?? []).map((s) => s.project_id))
      setSelectedRows((rows) => rows.filter((r) => !startedIds.has(r.projectId)))
      await refresh()
    } catch (e) {
      showNotification({
        title: 'Ошибка',
        message: e instanceof ApiError ? e.message : 'Не удалось запустить',
        color: 'red',
      })
    } finally {
      setStarting(false)
    }
  }

  const stopSession = async (sessionId: string) => {
    setStoppingId(sessionId)
    try {
      await api.loadTestStop({ session_id: sessionId })
      showNotification({ title: 'Остановлено', message: '', color: 'green' })
      await refresh()
    } catch (e) {
      showNotification({
        title: 'Ошибка',
        message: e instanceof ApiError ? e.message : DEFAULT_ERROR_NOTIFY_MESSAGE,
        color: 'red',
      })
    } finally {
      setStoppingId(null)
    }
  }

  const stopAll = async () => {
    setStoppingAll(true)
    try {
      await api.loadTestStop({ stop_all: true })
      showNotification({ title: 'Все тесты остановлены', message: '', color: 'green' })
      await refresh()
    } catch (e) {
      showNotification({
        title: 'Ошибка',
        message: e instanceof ApiError ? e.message : DEFAULT_ERROR_NOTIFY_MESSAGE,
        color: 'red',
      })
    } finally {
      setStoppingAll(false)
    }
  }

  return (
    <Stack gap="md">
      <Text size="sm" c="dimmed">
      Симулирует поток обращений к Мозгоботу. Можно запускать несколько проектов параллельно — у каждого свой
      Celery-драйвер и своя нагрузка. Пока тест идёт на одном проекте, можно добавить и запустить другие. Метрики
      обновляются при загрузке и каждую минуту.
      </Text>

      {sessions.length > 0 ? (
        <Stack gap="sm">
          <Group justify="space-between">
            <Text fw={600}>Активные и недавние тесты</Text>
            {runningSessions.length > 1 ? (
              <Button color="red" variant="light" size="xs" loading={stoppingAll} onClick={() => void stopAll()}>
                Остановить все
              </Button>
            ) : null}
          </Group>
          {sessions.map((sess) => (
            <SessionCard
              key={sess.session_id}
              sess={sess}
              errors={errorsBySession[sess.session_id]}
              errorsPage={errorsPageBySession[sess.session_id] ?? 1}
              onErrorsPageChange={(page) =>
                setErrorsPageBySession((prev) => ({ ...prev, [sess.session_id]: page }))
              }
              stopping={stoppingId === sess.session_id}
              onStop={() => void stopSession(sess.session_id)}
            />
          ))}
        </Stack>
      ) : (
        <Text size="sm" c="dimmed">
          Нет активных тестов.
        </Text>
      )}

      <Card withBorder padding="md">
          <Text fw={600} mb="sm">
            Запуск {running ? '(можно добавить другие проекты параллельно)' : ''}
          </Text>
          {running ? (
            <Text size="sm" c="dimmed" mb="sm">
              Уже идут тесты: {runningSessions.map((s) => s.project_name || s.project_id).join(', ')}. Новый
              запуск — только для проектов, которых нет в списке выше.
            </Text>
          ) : null}
          <Stack gap="md" maw={720}>
            <Text size="sm" fw={500}>
              Значения по умолчанию для новых проектов
            </Text>
            <Group grow align="flex-end">
              <NumberInput label="Обращений в час" value={defaultTph} onChange={setDefaultTph} min={1} max={500} />
              <NumberInput label="Сообщений мин" value={defaultMsgMin} onChange={setDefaultMsgMin} min={2} max={50} />
              <NumberInput label="Сообщений макс" value={defaultMsgMax} onChange={setDefaultMsgMax} min={2} max={50} />
              <Button variant="default" onClick={applyDefaultsToAll} disabled={!selectedRows.length}>
                Применить ко всем
              </Button>
            </Group>

            <Group align="flex-end" wrap="nowrap">
              <MultiSelect
                style={{ flex: 1 }}
                label="Добавить проекты"
                placeholder="Выберите один или несколько"
                data={availableToAdd.map((p) => ({ value: p.id, label: p.name }))}
                value={addProjectIds}
                onChange={setAddProjectIds}
                disabled={loading || !availableToAdd.length}
                searchable
                clearable
              />
              <Button onClick={addProjectsFromSelect} disabled={!addProjectIds.length}>
                Добавить
              </Button>
            </Group>

            {selectedRows.length > 0 ? (
              <Table withTableBorder striped>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Проект</Table.Th>
                    <Table.Th>Обращ./час</Table.Th>
                    <Table.Th>Сообщ. мин</Table.Th>
                    <Table.Th>Сообщ. макс</Table.Th>
                    <Table.Th w={40} />
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {selectedRows.map((row) => {
                    const alreadyRunning = runningProjectIds.has(row.projectId)
                    return (
                    <Table.Tr key={row.projectId} style={alreadyRunning ? { opacity: 0.55 } : undefined}>
                      <Table.Td>
                        {row.name}
                        {alreadyRunning ? (
                          <Text size="xs" c="dimmed" component="span">
                            {' '}
                            (тест уже идёт)
                          </Text>
                        ) : null}
                      </Table.Td>
                      <Table.Td>
                        <NumberInput
                          size="xs"
                          value={row.tph}
                          onChange={(v) => updateRow(row.projectId, { tph: v })}
                          min={1}
                          max={500}
                          hideControls
                        />
                      </Table.Td>
                      <Table.Td>
                        <NumberInput
                          size="xs"
                          value={row.msgMin}
                          onChange={(v) => updateRow(row.projectId, { msgMin: v })}
                          min={2}
                          max={50}
                          hideControls
                        />
                      </Table.Td>
                      <Table.Td>
                        <NumberInput
                          size="xs"
                          value={row.msgMax}
                          onChange={(v) => updateRow(row.projectId, { msgMax: v })}
                          min={2}
                          max={50}
                          hideControls
                        />
                      </Table.Td>
                      <Table.Td>
                        <ActionIcon
                          variant="subtle"
                          color="red"
                          aria-label="Удалить"
                          onClick={() => removeRow(row.projectId)}
                        >
                          <IconTrash size={16} />
                        </ActionIcon>
                      </Table.Td>
                    </Table.Tr>
                    )
                  })}
                </Table.Tbody>
              </Table>
            ) : (
              <Text size="sm" c="dimmed">
                Добавьте проекты с AI и настройте нагрузку для каждого.
              </Text>
            )}

            <Button
              loading={starting}
              onClick={() => void start()}
              disabled={
                !selectedRows.some((r) => !runningProjectIds.has(r.projectId))
              }
            >
              Запустить тест (
              {selectedRows.filter((r) => !runningProjectIds.has(r.projectId)).length}{' '}
              {selectedRows.filter((r) => !runningProjectIds.has(r.projectId)).length === 1
                ? 'проект'
                : 'проектов'}
              )
            </Button>
          </Stack>
        </Card>
    </Stack>
  )
}

function SessionCard({
  sess,
  errors,
  errorsPage,
  onErrorsPageChange,
  stopping,
  onStop,
}: {
  sess: LoadTestSessionStatus
  errors?: LoadTestErrorsResponse
  errorsPage: number
  onErrorsPageChange: (page: number) => void
  stopping: boolean
  onStop: () => void
}) {
  const isRunning = sess.status === 'running'
  const errTotalPages = errors ? Math.max(1, Math.ceil(errors.total / errors.per_page)) : 1

  return (
    <Card withBorder padding="md">
      <Group justify="space-between" mb="sm">
        <div>
          <Text fw={600}>{sess.project_name || sess.project_id}</Text>
          <Text size="xs" c="dimmed">
            {sess.tickets_per_hour} обр./ч · сообщ. {sess.messages_per_ticket_min}–{sess.messages_per_ticket_max}
          </Text>
        </div>
        <BadgeInline running={isRunning} />
      </Group>
      <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="sm">
        <Stat
          label="Обращений за период"
          value={String(sess.tickets_created_in_period ?? sess.tickets_created ?? 0)}
          hint={sess.duration_minutes != null ? `за ${sess.duration_minutes} мин` : undefined}
        />
        <Stat
          label="Фактически в час"
          value={sess.tickets_per_hour_actual != null ? String(Math.round(sess.tickets_per_hour_actual)) : '—'}
        />
        <Stat label="Завершено" value={String(sess.tickets_completed ?? 0)} />
        <Stat
          label="Ошибки (сбой задач)"
          value={String(sess.tickets_failed ?? 0)}
          hint="передача оператору — не ошибка"
        />
        <Stat
          label="За последнюю мин."
          value={`${sess.last_minute_tickets ?? 0} / ${sess.last_minute_ai_responses ?? 0} AI`}
        />
      </SimpleGrid>
      <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="sm" mt="md">
        <Stat label="Среднее до 1-го ответа" value={fmtSec(sess.avg_time_to_first_response_sec)} />
        <Stat label="Среднее до 2-го ответа" value={fmtSec(sess.avg_time_to_second_response_sec)} />
        <Stat label="Среднее до 3-го ответа" value={fmtSec(sess.avg_time_to_third_response_sec)} />
        <Stat label="P95 до 1-го" value={fmtSec(sess.p95_time_to_first_response_sec)} />
        <Stat label="P95 до 2-го" value={fmtSec(sess.p95_time_to_second_response_sec)} />
        <Stat label="P95 до 3-го" value={fmtSec(sess.p95_time_to_third_response_sec)} />
      </SimpleGrid>

      {errors && errors.items.length > 0 ? (
        <Stack gap="xs" mt="md">
          <Text fw={600} c="red" size="sm">
            Ошибки
          </Text>
          <Table striped withTableBorder>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Обращение</Table.Th>
                <Table.Th>Ошибка</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {errors.items.map((it, i) => {
                const slug = it.project_slug
                const num = it.ticket_number
                const href =
                  slug && num != null ? `/tickets/${slug}-${num}` : it.ticket_id ? `/tickets/${it.ticket_id}` : '#'
                const label =
                  slug && num != null ? `${slug.toUpperCase()}-${num}` : it.ticket_id?.slice(0, 8) ?? '—'
                return (
                  <Table.Tr key={`${it.ticket_id}-${i}`}>
                    <Table.Td>
                      <Text component={Link} to={href} size="sm" c="blue" style={{ textDecoration: 'none' }}>
                        {label}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{it.failed_reason || '—'}</Text>
                    </Table.Td>
                  </Table.Tr>
                )
              })}
            </Table.Tbody>
          </Table>
          {errors.total > errors.per_page ? (
            <Group gap="xs">
              <Text size="xs" c="dimmed">
                Стр. {errors.page} из {errTotalPages} (всего {errors.total})
              </Text>
              <Button
                size="compact-xs"
                variant="default"
                disabled={errors.page <= 1}
                onClick={() => onErrorsPageChange(Math.max(1, errorsPage - 1))}
              >
                Назад
              </Button>
              <Button
                size="compact-xs"
                variant="default"
                disabled={errors.page >= errTotalPages}
                onClick={() => onErrorsPageChange(errorsPage + 1)}
              >
                Вперёд
              </Button>
            </Group>
          ) : null}
        </Stack>
      ) : null}

      {isRunning ? (
        <Button mt="md" color="red" variant="light" size="sm" loading={stopping} onClick={onStop}>
          Остановить этот проект
        </Button>
      ) : null}
    </Card>
  )
}

function BadgeInline({ running }: { running: boolean }) {
  return (
    <Text size="sm" c={running ? 'green' : 'dimmed'}>
      {running ? 'Запущен' : 'Остановлен'}
    </Text>
  )
}

function Stat({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div>
      <Text size="xs" c="dimmed">
        {label}
      </Text>
      <Text fw={600} size="lg">
        {value}
      </Text>
      {hint ? (
        <Text size="xs" c="dimmed">
          {hint}
        </Text>
      ) : null}
    </div>
  )
}

function fmtSec(v: number | null | undefined) {
  if (v == null) return '—'
  return `${v} с`
}
